<template>
  <list-layout
      :title="$t('fees') | startcase"
      @loaded="onLoaded"
      @loading="onLoading"
      @pagination="onPagination"
      @sorted="onSorted"
      no-counts
      no-search
      no-apply-btn
  >
    <template v-slot:heading-right>
      <b-button
        v-if="permissions.pages[Pages.FeesList].canWrite"
        class="ml-4"
        variant="secondary"
        :to="{'name': 'fees-create'}"
      >{{ $t("add_fee") | startcase }}</b-button>
    </template>
    <template v-slot:beforeFilters>
      <b-row>
        <b-col>
          <filter-builder
              ref="feesFilters"
              :id="userPreferenceNames.FEE_LIST_FILTER"
              :filters="getFiltersWithOptions()"
              :apply-filters="applyFilters"
              :reset-filters="resetFilters"
              :filters-pending="filtersPending"
              :filters-applied="filtersApplied"
              @sorted="onSorted"
              @filter="onFilter"
          />
        </b-col>
      </b-row>
      <b-row class="text-center mb-3" align-v="center" align-h="center">
        <b-col cols="5">
          <b-form-radio-group
              v-model="feesTable.selectedTab"
              :options="feesTable.tab"
              buttons
              button-variant="outline-secondary"
              name="radios-btn-outline"
              class="radio-buttons"
          />
        </b-col>
      </b-row>
    </template>

    <template v-slot:table>
      <t-table
          :columns="columns"
          :options="options"
          :limit="limit"
          ref="feelist"
          id="fees-list-table"
          @loaded="onLoaded"
          @sorted="onSorted"
          @pagination="onPagination"
          @loading="onLoading"
      >
        <template v-slot:counts>
          <div class="d-flex">
            <pagination
                class="smaller p-0"
                :page="page"
                :count="count"
                :loading="loading"
                v-model="limit"
                @limit="onLimit"
            />
          </div>
        </template>

        <!-- tariffs codes -->
        <template v-slot:tariff="{ row }">
          <NamesListModal class-names="table-meta mb-2" :values="row.scope.tariffs.codes" modalTitle="Tariffs"/>
        </template>

        <!-- Period start date -->
        <template v-slot:periodStartDate="{ row }">
          {{ row.scope.period.start }}
        </template>

        <!-- Period end date -->
        <template v-slot:periodEndDate="{ row }">
          {{ row.scope.period.end }}
        </template>

        <!-- Eff. calendar start date -->
        <template v-slot:effectiveStartDate="{ row }">
          {{ row.scope.period.start }}
        </template>

        <!-- Eff. calendar start date -->
        <template v-slot:effectiveEndDate="{ row }">
          {{ row.scope.period.end }}
        </template>

        <!-- Type -->
        <template v-slot:type="{ row }">
          {{ row.type | capitalize }}
        </template>

        <!-- Rights Holders -->
        <template v-slot:rightsHolder="{ row }">
          <NamesListModal
              class-names="table-meta mb-2"
              :text-plural="$t('rights_holders')"
              :text-exception="$t('all_except') | capitalize"
              :text-none="$t('all') | capitalize"
              :sort="true"
              :exception="row.scope.rightsHolders.select === 'exclude'"
              :values="row.scope.rightsHolders.ids"
          />
        </template>

        <!-- Territory of incoming royalty -->
        <template v-slot:country="{ row }">
          {{ row.scope.country }}
        </template>

        <!-- Societies -->
        <template v-slot:society="{ row }">
          <NamesListModal class-names="table-meta mb-2" :values="row.scope.societies.ids" modalTitle="Societies"/>
        </template>

        <!-- rate -->
        <template v-slot:rate="{ row }">
          <span>{{ row.rate }}%</span>
        </template>

        <!-- Action -->
        <template v-slot:action="{ row }">
          <span :id="'fee-action-'+row.id" v-if="permissions.pages[Pages.FeesList].canWrite">
            <fa-icon
              role="button"
              size="1x"
              :title="$t('edit') | lowercase"
              :icon="['fa', 'pen']"
              class="cursor-pointer mr-3"
              @click="onEdit(row.id)"
            />
            <fa-icon
              role="button"
              size="1x"
              :title="$t('delete') | lowercase"
              :icon="['fa', 'trash']"
              class="cursor-pointer"
              color="#c23d5b"
              @click="onDelete(row.id)"
            />
          </span>
        </template>
      </t-table>
    </template>
    <!-- Edit Fee Modal -->
    <modal
      id="edit-modal"
      :customTitle="$t('edit_fee') | capitalize"
      modal-class="custom-modal"
      modalType="edit"
      size="xl"
      centered
      hide-footer
    >
      <edit-fee-form
        :feeId="activeModalId"
        :feeType="computedFeeType"
        @feeUpdated="feeUpdated()"
      />
    </modal>
    <!-- Deletion Confirmation Modal -->
    <modal
      id="confirmation-modal"
      :customTitle="$t('remove_transaction') | capitalize"
      modal-class="custom-modal"
      modalType="info"
      size="md"
      centered
      @ok="onConfirmDelete()"
      okTitle="Confirm"
    >
      <div>
        {{ "You are about to remove fee. Are you sure you want to proceed?" }}
      </div>
    </modal>
  </list-layout>
</template>

<script>
import { capitalize, get, snakeCase } from "lodash"
import {
  filterTypes,
  linkTypes,
  perPageItemsLg,
  userPreferenceNames
} from "@/constants"
import { mapActions, mapMutations, mapState } from "vuex"
import EditFeeForm from "@/pages/Fees/EditFeeForm"
import FilterBuilder from "@/components/FilterBuilder"
import NamesListModal from "@/pages/Repertoire/Recordings/NamesListModal"
import { Pages } from "@/utils/pages"
import Pagination from "@/components/Pagination"
import { listRouteMixin } from "@/utils/mixins"


export default {
  name: "ListFeesPage",
  mixins: [listRouteMixin],
  components: {
    EditFeeForm,
    FilterBuilder,
    Pagination,
    NamesListModal
  },
  data () {
    return {
      page: 1,
      count: 0,
      limit: perPageItemsLg,
      loading: false,
      userPreferenceNames,
      capitalize,
      get,
      Pages,
      linkTypes,
      activeModalId: "",
      feesTable: {
        tab: [
          {
            value: "base_fees",
            text: capitalize(this.$t("base_fees")),
          },
          {
            value: "service_fees",
            text: capitalize(this.$t("service_fees")),
          },
          {
            value: "international_incoming",
            text: capitalize(this.$t("international_incoming")),
          },
        ],
        selectedTab: null,
      },
      filters: {
        rate: {
          value: "",
          defaultValue: null,
          type: filterTypes.TEXT,
          label: capitalize(this.$t("rate"))
        },
        tariff: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("tariff")),
          type: filterTypes.SELECT,
          options: [],
          hidden: false
        },
        periodStartDate: {
          value: null,
          defaultValue: null,
          type: filterTypes.DATE,
          format: "YYYY-MM-DD",
          label: capitalize(this.$t("usage_start_date")),
          valueType: this.$config.ISO_DATE_FORMAT,
        },
        periodEndDate: {
          value: null,
          defaultValue: null,
          type: filterTypes.DATE,
          format: "YYYY-MM-DD",
          label: capitalize(this.$t("usage_end_date")),
          valueType: this.$config.ISO_DATE_FORMAT,
        },
        society: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("society")),
          type: filterTypes.SELECT,
          options: [],
        },
        rightsHolder: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT_SEARCH,
          label: capitalize(this.$t("rights_holder")),
          placeholder: capitalize(this.$t("search_rightsholder")),
          excludeName: "id",
          valueField: "resoundId",
          textField: "name",
          searchName: "search",
          listEndpoint: this.$api.rightsholders.rightsholdersList,
          noWatch: true,
        },
        country: {
          value: null,
          defaultValue: null,
          label: capitalize(this.$t("territory_of_incoming_royalties")),
          type: filterTypes.SELECT,
          options: [],
          hidden: true
        }
      },
      options: {
        sortable: [
          "periodStartDate",
          "periodEndDate",
          "effectiveStartDate",
          "effectiveEndDate",
          "rate",
          "country"
        ],
        headings: {
          tariff: capitalize(this.$t("tariff")),
          periodStartDate: capitalize(this.$t("usage_start_date")),
          periodEndDate: capitalize(this.$t("usage_end_date")),
          effectiveStartDate: capitalize(this.$t("effective_calendar_start_date")),
          effectiveEndDate: capitalize(this.$t("effective_calendar_end_date")),
          type: capitalize(this.$t("type")),
          rightsHolder: capitalize(this.$t("rightsholder")),
          society: capitalize(this.$t("society")),
          rate: capitalize(this.$t("rate")),
          country: capitalize(this.$t("territory_of_incoming_royalties")),
          action: "",
        },
        columnsClasses: {},
        responseAdapter ({ data }) {
          return {
            data: data.results,
            count: data.count,
          }
        },
        requestFunction () {
          let queryParams = { ...this.$route.query }
          this.page = Number(get(queryParams, "page", 1))
          return this.$api.fees.feesList(queryParams)
        },
      },
      filterCache: {
        tariff: null,
        country: null
      }
    }
  },
  methods: {
    ...mapActions("consts", ["getTariffs", "getCountries"]),
    ...mapMutations("alert", ["success", "error"]),
    onLoaded ({ data }) {
      this.count = data.count
      this.summary = data.summary
      this.loading = false
    },
    onPagination (page) {
      this.page = page
      this.updateRouterPagination("page", page)
    },
    getData () {
      this.$refs.feelist.getData()
    },
    onSorted (data) {
      this.queryParams = {
        ...this.queryParams,
        ["sorting"]: data.map(item => item.ascending ? toSortParam(item.column) : `-${toSortParam(item.column)}`)
      }
      this.applyFilters()
    },
    getFiltersWithOptions () {
      this.filters.tariff.options = this.tariffOptions
      this.filters.society.options = this.memberCollectivesOptions
      this.filters.country.options = this.countriesOptions

      if (this.feesTable.selectedTab === "international_incoming") {
        this.filters.periodStartDate.label = capitalize(this.$t("effective_calendar_start_date"))
        this.filters.periodEndDate.label = capitalize(this.$t("effective_calendar_end_date"))
        this.filters.country.hidden = false
        this.filters.tariff.hidden = true
      } else {
        this.filters.periodStartDate.label = capitalize(this.$t("usage_start_date"))
        this.filters.periodEndDate.label = capitalize(this.$t("usage_end_date"))
        this.filters.country.hidden = true
        this.filters.tariff.hidden = false
      }

      return this.filters
    },
    onFilter (name, value) {
      if (name === "rightsHolder") {
        if (value === null) {
          this.changeFilter(name, value)
        } else {
          this.changeFilter(name, value.resoundId)
        }
      }
    },
    onDelete (id) {
      this.activeModalId = id
      this.$bvModal.show("confirmation-modal")
    },
    onConfirmDelete () {
      this.$api.fees.deleteFee(this.activeModalId)
        .then(() => {
          this.success("Fee has been removed successfully.")
          this.getData()
          this.activeModalId = ""
        }).catch(error => {
          const { data, status } = error.response
          let message = ""
          if (status === 400) {
            message = capitalize(data[0])
          } else if (status === 422) {
            message = "Failed to update fee. Please correct the data and try again."
            if (data.nonFieldErrors) {
              message = message + "\n" + data.nonFieldErrors.map(e => e.message).join("\n")
            }
          } else {
            message = "Failed to update fee. Please contact administrator or try again."
          }
          this.error(message)
        })
    },
    onEdit (id) {
      this.activeModalId = id
      this.$bvModal.show("edit-modal")
    },
    feeUpdated (){
      this.$bvModal.hide("edit-modal")
      this.activeModalId = ""
      this.getData()
    }
  },
  computed: {
    ...mapState("user", ["user", "permissions"]),
    ...mapState("consts", ["countries", "tariffs", "memberCollectives"]),
    columns () {
      let cols = [
        "tariff",
        "periodStartDate",
        "periodEndDate",
        "effectiveStartDate",
        "effectiveEndDate",
        "type",
        "rightsHolder",
        "country",
        "society",
        "rate",
        "action",
      ]
      if (this.feesTable.selectedTab === "international_incoming") {
        const notInternationalColumns = ["periodStartDate", "periodEndDate", "tariff"]
        cols = cols.filter(col => !notInternationalColumns.includes(col))
      } else {
        const internationalColumns = ["effectiveStartDate", "effectiveEndDate", "country"]
        cols = cols.filter(col => !internationalColumns.includes(col))
      }
      return cols
    },
    tariffOptions () {
      let options = this.tariffs.map(c => {
        return { text: c.text, value: c.code }
      })
      options.unshift({ text: capitalize(this.$t("all")), value: null })
      return options
    },
    memberCollectivesOptions () {
      let options = this.memberCollectives.map(c => {
        return { text: c.name, value: c.name }
      })
      options.unshift({ text: capitalize(this.$t("all")), value: null })
      return options
    },
    countriesOptions () {
      let options = this.countries.map(c => {
        return { text: c.text, value: c.alpha3 }
      })
      options.unshift({ text: capitalize(this.$t("all")), value: null })
      return options
    },
    computedFeeType () {
      if (this.feesTable.selectedTab) {
        return this.feesTable.selectedTab.split("_")[0]
      }
      return ""
    }
  },
  watch: {
    "feesTable.selectedTab": function (newVal) {
      this.queryParams = {
        ...this.queryParams, "type": newVal.split("_")[0]
      }
      delete this.queryParams["sorting"]
      this.applyFilters()
    },
  },
  mounted () {
    this.updateTableSortIcons(this.$refs.feelist.$children[0], true)
    this.feesTable.selectedTab = "base_fees"
    if (!this.tariffs.length) {
      this.getTariffs()
    }
    if (!this.countries.length) {
      this.getCountries()
    }
  },
}

function toSortParam (param) {
  return snakeCase(param)
}
</script>